import { yupResolver } from '@hookform/resolvers/yup';
import { Alert, Box, Grid } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import {
  ClientContactDTO,
  ClientDTO,
  ClientStatusDTO,
  createIndustry,
  findByName,
  findClientByLinkedinUrl,
  getClientById,
  getClientFromDirectHire,
  getClientStatusListByVisibility,
  getIndustries,
} from 'wisestep-api';
import { ControlledTextInput, IUserDTO, WsButton, WsSelectObj } from 'wisestep-base';
import { object, string } from 'yup';

import { useUserContext, useWorkspaceContext } from '../../../context';
import { AGILE_VENTURE_WORKSPACE_ID } from '../../../utils/Constants';
import {
  mapDirectHireClientToClientContactDTO,
  mapDirectHireClientToClientDTO,
  mergeFetchedClients,
} from '../helper/ClientFormHelper';
import { linkedInRegex } from '../helper/ClientListingHelper';
import { DirectHireClient } from '../types/DirectHireClient';

const validationSchema = object().shape({
  name: string().required('Please provide Name'),
  linkedinUrl: string().required('Please provide LinkedIn URL').matches(linkedInRegex, {
    excludeEmptyString: true,
    message: 'Please provide a valid LinkedIn URL',
  }),
});

interface ClientDetailsFormProps {
  client: ClientDTO;
  setClient: (client: ClientDTO) => void;
  setClientFoundInDirectHire: (clientFoundInDirectHire: boolean) => void;
  setFetchedContacts: (fetchedContacts: ClientContactDTO[]) => void;
  setFormWizardCurrentChildStep: (formWizardCurrentChildStep: number) => void;
}

const ClientBasicDetailsForm = ({
  client,
  setClient,
  setClientFoundInDirectHire,
  setFetchedContacts,
  setFormWizardCurrentChildStep,
}: ClientDetailsFormProps) => {
  const workspace = useWorkspaceContext();
  const params = useParams();
  const loggedInUser = useUserContext().userDTO as IUserDTO;
  const [industryOptions, setIndustryOptions] = useState<WsSelectObj[]>([]);
  const [defaultClientStatus, setDefaultClientStatus] = useState<ClientStatusDTO>();
  const [existingClient, setExistingClient] = useState<ClientDTO | null>(null);

  const allowedWorkspaces = [
    'a61ed280-61fa-4e19-8e54-88bffcc24a75',
    'c809aa49-c696-4ea1-8467-0108a16ab786',
  ];

  const methods = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: client || {},
  });
  const { handleSubmit, getValues, reset, watch } = methods;
  const isWorkspaceAllowed = workspace?.id !== AGILE_VENTURE_WORKSPACE_ID;

  const name = watch('name');
  const linkedinUrl = watch('linkedinUrl');

  useEffect(() => {
    if (name || linkedinUrl) {
      setExistingClient(null);
    }
  }, [name, linkedinUrl]);

  const getIndustryData = async () => {
    const response = await getIndustries();
    if (response?.data) {
      setIndustryOptions(response?.data);
    }
  };

  useEffect(() => {
    getIndustryData();
  }, []);

  useEffect(() => {
    const getClientStatusListDetails = async () => {
      const response = await getClientStatusListByVisibility();
      const optionsArray: WsSelectObj[] = [];
      let defaultStatusName = '';
      response.data.forEach((obj: ClientStatusDTO) => {
        if (obj.makeDefaultStatus) {
          defaultStatusName = obj.id;
          methods.setValue('clientStatusId', obj.id);
          setDefaultClientStatus(obj);
        }
        optionsArray.push({
          label: obj.clientStatusName as string,
          value: obj.id ?? '',
        });
      });
      if (client?.clientStatus?.clientStatusName) {
        const selectedOption = optionsArray.find(
          (item) => item.label === client?.clientStatus?.clientStatusName
        );
        if (selectedOption) {
          methods.setValue('clientStatusId', selectedOption.value as string);
        }
      } else {
        methods.setValue('clientStatusId', defaultStatusName);
      }
    };

    getClientStatusListDetails();
  }, []);

  const fetchClient = useCallback(() => {
    (async () => {
      if (params.clientId) {
        const response = await getClientById(params.clientId);
        if (response.data.data) {
          const fetchedClient: ClientDTO = response.data.data;
          fetchedClient.clientStatusId = fetchedClient.clientStatus?.id;
          setClient(fetchedClient);
          reset(fetchedClient);
        }
      }
    })();
  }, [params.clientId, reset]);

  useEffect(() => {
    fetchClient();
  }, [fetchClient]);

  const onProceed = async (data: ClientDTO) => {
    const nameResponse = await findByName(data.name);
    if (nameResponse.data.data && nameResponse.data.data.id != getValues().id) {
      setExistingClient(nameResponse.data.data);
      return;
    }

    const linkedinResponse = await findClientByLinkedinUrl(data.linkedinUrl || '');
    if (linkedinResponse.data.data && linkedinResponse.data.data.id != getValues().id) {
      setExistingClient(linkedinResponse.data.data);
      return;
    }

    setExistingClient(null);

    if (!params.clientId && workspace?.id && allowedWorkspaces.includes(workspace?.id)) {
      const linkedinUrl = getValues().linkedinUrl?.replace(/\/$/, '') || '';
      const response = await getClientFromDirectHire({
        query: linkedinUrl,
        searchType: 'exact',
        searchBy: 'linkedinUrl',
      });
      if (response && response.data) {
        const directHireClients: DirectHireClient[] = response.data as DirectHireClient[];
        if (directHireClients.length > 0) {
          setClientFoundInDirectHire(true);
          const mergedClient = mergeFetchedClients(directHireClients);
          const clientDTO = mapDirectHireClientToClientDTO(mergedClient);
          const clientContacts = mapDirectHireClientToClientContactDTO(mergedClient);
          setFetchedContacts(clientContacts);
          if (clientDTO.industry) {
            const industry = industryOptions.find((option) => option.label === clientDTO.industry);
            if (!industry) {
              const industryResp = await createIndustry({
                label: clientDTO.industry,
                source: 'directHire',
              });
              if (industryResp && industryResp.data) {
                clientDTO.industry = industryResp.data.value;
              }
            } else {
              clientDTO.industry = industry.value as string;
            }
          }
          clientDTO.clientStatusId = defaultClientStatus?.id;
          clientDTO.clientStatus = defaultClientStatus;
          clientDTO.owner = getClientOwner();
          clientDTO.ownerId = getClientOwner()?.email || '';
          setClient(clientDTO);
          setFormWizardCurrentChildStep(1);
        } else {
          setClient({
            ...client,
            name: getValues().name,
            linkedinUrl: linkedinUrl,
            clientStatusId: defaultClientStatus?.id,
            clientStatus: defaultClientStatus,
            owner: getClientOwner(),
            ownerId: getClientOwner()?.email || '',
          });
          setFetchedContacts([]);
          setClientFoundInDirectHire(false);
          setFormWizardCurrentChildStep(1);
        }
      }
    } else {
      data.owner = getClientOwner();
      data.ownerId = getClientOwner()?.email || '';
      setClient(data);
      setFormWizardCurrentChildStep(1);
    }
  };

  const getClientOwner = () => {
    if (getValues().owner) {
      return getValues().owner;
    }
    return {
      name: loggedInUser.firstName + ' ' + loggedInUser.lastName,
      email: loggedInUser.email || '',
    };
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onProceed)}>
        <Box borderRadius={1.5}>
          <Grid container py={1}>
            <Grid item p={1} sm={6}>
              <ControlledTextInput
                name="name"
                type="text"
                fullWidth
                labelText="Client Name"
                variant="outlined"
                showMandatorySymbol={true}
              />
            </Grid>
            <Grid item p={1} sm={6}>
              <ControlledTextInput
                name="linkedinUrl"
                type="text"
                fullWidth
                labelText="Linkedin Url"
                required={isWorkspaceAllowed}
                showMandatorySymbol={isWorkspaceAllowed}
              />
            </Grid>
            {existingClient && (
              <Grid item p={1} sm={12}>
                <Alert severity="error">
                  {'The client you are trying to add already exists.' +
                    `${
                      existingClient?.owner && existingClient?.owner?.email !== loggedInUser.email
                        ? ` Please contact ${existingClient?.owner?.name} (${existingClient?.owner?.email}) to get access.`
                        : ''
                    }`}
                </Alert>
              </Grid>
            )}
            <Grid item p={1} sm={12} display="flex" justifyContent="flex-end">
              <WsButton type="submit" variant="contained">
                Proceed
              </WsButton>
            </Grid>
          </Grid>
        </Box>
      </form>
    </FormProvider>
  );
};

export default ClientBasicDetailsForm;
